import { PureComponent, createRef, memo } from 'react';
import PropTypes, { arrayOf, shape, string } from 'prop-types';
import { nanoid } from 'nanoid';
import { Form, Field } from 'react-final-form';

import { errorTypes, Messages } from '~/components';
import {
  PRODUCT_TYPE,
  PRODUCT_INFORMATION,
  PRODUCT_QUANTITY,
  CANCEL_BUTTON,
} from './fields-configs';
import { getProductTypesOptions } from '../../utils';
import { analyticsListeners } from '~/analytics';

import styles from './ProductsForm.scss';

export const getProductFormConfig = (props) => {
  const baseConfig = {
    submitButtonTitle: 'Add product',
    onSubmitHandler: props.onSubmitHandler,
  };

  switch (true) {
    // config for edit form
    case !!props.editableProduct:
      return {
        ...baseConfig,
        submitButtonTitle: 'Save',
        onSubmitHandler: props.onSaveHandler,
      };
    // config for add form
    default:
      return baseConfig;
  }
};

const initialSelectedProduct = (product) => ({
  id: '',
  type: '',
  name: '',
  quantity: 1,
  information: '',
  adminTime: null,
  activityTime: null,
  selectionType: null,
  ...product,
});

const validateField = (value, field) => {
  if (field.required && (!value || value === '')) {
    return field.validationMessageKeys?.required || `${field.label || field.name} is required`;
  }
  if (value && field.regex && !field.regex.test(value)) {
    return field.validationMessageKeys?.regex || `${field.label || field.name} is invalid`;
  }
  if (value && field.maxLength && value.length > field.maxLength) {
    return field.validationMessageKeys?.maxLength || `${field.label || field.name} is too long`;
  }
  return undefined;
};

export class ProductsForm extends PureComponent {
  constructor(props) {
    super(props);
    this.formRef = createRef();
    this.configuration = getProductFormConfig(props);
    this.state = {
      productSelectionTypes: [],
      selectedProduct: this.getSelectedProduct(),
      fields: {
        id: props?.editableProduct?.id || '',
        selectionTypeId: props?.editableProduct?.selectionType?.id || '',
        information: props?.editableProduct?.information || '',
        quantity: props?.editableProduct?.quantity || '1',
      },
    };
  }

  getSelectedProduct = () => {
    const { products, editableProduct } = this.props;

    const product = products[0];

    return products.length === 1
      ? {
          ...initialSelectedProduct(),
          id: product.id,
          type: product.type,
          name: product.name,
          adminTime: product.adminTime,
          activityTime: product.activityTime,
        }
      : initialSelectedProduct(editableProduct);
  };

  onChangeTypeHandler = ({ target }) => {
    const { selectedProduct, fields } = this.state;
    const { products, clearErrorMessages } = this.props;
    const currentProduct = products.find((product) => product.id === target.value);
    const { id, activityTime, adminTime, name, type, selectionTypes } = currentProduct;

    clearErrorMessages();
    this.setState({
      productSelectionTypes: selectionTypes,
      selectedProduct: {
        ...selectedProduct,
        id,
        activityTime,
        adminTime,
        name,
        type,
      },
      fields: {
        ...fields,
        id: target.value,
      },
    });
  };

  onChangeSelectionTypesHandler = ({ target }) => {
    const { selectedProduct, fields } = this.state;
    const { products, clearErrorMessages } = this.props;
    let selectedType = !selectedProduct.id && target.value;

    if (selectedProduct.id) {
      selectedType = products
        .find((product) => product.id === selectedProduct.id)
        .selectionTypes.find((selectionType) => selectionType.id === target.value);
    }

    clearErrorMessages();
    this.setState({
      selectedProduct: {
        ...selectedProduct,
        selectionType: selectedType,
      },
      fields: {
        ...fields,
        selectionTypeId: target.value,
      },
    });
  };

  onChangeProductInformation = ({ target }) => {
    const { selectedProduct, fields } = this.state;
    const { clearErrorMessages } = this.props;

    clearErrorMessages();
    this.setState({
      selectedProduct: {
        ...selectedProduct,
        information: target.value,
      },
      fields: {
        ...fields,
        information: target.value,
      },
    });
  };

  onChangeProductQuantity = ({ target }) => {
    const { selectedProduct, fields } = this.state;
    const { clearErrorMessages } = this.props;

    clearErrorMessages();
    this.setState({
      selectedProduct: {
        ...selectedProduct,
        quantity: target.value,
      },
      fields: {
        ...fields,
        quantity: target.value,
      },
    });
  };

  onSubmitHandler = (data) => {
    const { products, editableProduct } = this.props;
    const { selectedProduct } = this.state;

    const { productId: id = products[0].id, information, quantity, selectionTypeId } = data;

    const productSelectionType = products
      .find((product) => product.id === id)
      .selectionTypes.find((selectionType) => selectionType.id === selectionTypeId);
    const product = {
      ...selectedProduct,
      information,
      quantity: parseInt(quantity, 10) || selectedProduct.quantity,
      selectionType: productSelectionType,
      productId: editableProduct?.productId || nanoid(),
    };

    analyticsListeners.triggerAddProductAnalytics(product);
    this.configuration.onSubmitHandler(product);
  };

  /**
   * @description this triggers the submit and validation of the form
   * useful if you want to trigger the validation manually
   */
  onFakeSubmitHandler = () => {
    if (this.formRef.current) {
      this.formRef.current.submit();
    }
  };

  onErrorHandler = () => {
    const { clearErrorMessages } = this.props;

    clearErrorMessages();
  };

  validateForm = (values) => {
    const { productSelectionTypes, selectedProduct } = this.state;
    const { products } = this.props;
    const errors = {};

    const hasProductSelectionType =
      !selectedProduct.id ||
      products.find((product) => product.id === selectedProduct.id).selectionTypes.length > 0;

    const defaultProductSelectionTypes = products?.[0]?.selectionTypes ?? [];
    const selectionTypes =
      productSelectionTypes.length > 0 ? productSelectionTypes : defaultProductSelectionTypes;

    // Validate productId if products.length !== 1
    if (products.length !== 1) {
      const productTypeConfig = {
        required: true,
        validationMessageKeys: {
          required: `Please ${this.props.configs.productTypeLabelForDefaultOption?.toLowerCase() || 'select a product type'}`,
        },
      };
      const error = validateField(values.productId, productTypeConfig);
      if (error) errors.productId = error;
    }

    // Validate selectionTypeId if hasProductSelectionType
    if (hasProductSelectionType && selectionTypes.length > 0) {
      const selectionTypeConfig = {
        required: true,
        validationMessageKeys: {
          required: 'Please select a selection type',
        },
      };
      const error = validateField(values.selectionTypeId, selectionTypeConfig);
      if (error) errors.selectionTypeId = error;
    }

    // Validate information
    const informationConfig = {
      ...PRODUCT_INFORMATION,
      validationMessageKeys: {
        required:
          this.props.configs.productInformationValidationMessage ||
          'Please enter your selected product',
      },
    };
    const informationError = validateField(values.information, informationConfig);
    if (informationError) errors.information = informationError;

    // Validate quantity (optional but check if present)
    const quantityConfig = PRODUCT_QUANTITY;
    const quantityError = validateField(values.quantity, quantityConfig);
    if (quantityError) errors.quantity = quantityError;

    return errors;
  };

  render() {
    const { productSelectionTypes, selectedProduct, fields } = this.state;
    const {
      products,
      onClickCancelHandler,
      configs: {
        productTypeLabel,
        productTypeLabelForDefaultOption,
        quantityLabel,
        productInformationLabel,
        productInformationInstruction,
        productInformationValidationMessage,
        getProductSelectionTypesConfig,
      },
    } = this.props;

    const { submitButtonTitle } = this.configuration;

    const hasProductSelectionType =
      !selectedProduct.id ||
      products.find((product) => product.id === selectedProduct.id).selectionTypes.length > 0;

    const productTypeConfig = {
      ...PRODUCT_TYPE,
      label: productTypeLabel,
      options: getProductTypesOptions(products, productTypeLabelForDefaultOption),
      validationMessageKeys: {
        required: `Please ${productTypeLabelForDefaultOption.toLowerCase()}`,
      },
      onChange: this.onChangeTypeHandler,
    };

    const defaultProductSelectionTypes = products?.[0]?.selectionTypes ?? [];
    const productSelectionTypesConfig = hasProductSelectionType
      ? {
          ...getProductSelectionTypesConfig(
            productSelectionTypes.length > 0 ? productSelectionTypes : defaultProductSelectionTypes,
            !!selectedProduct.selectionType,
          ),
          name: 'selectionTypeId',
          id: 'selectionTypeId',
          onChange: this.onChangeSelectionTypesHandler,
        }
      : {
          id: nanoid(),
          required: false,
        };

    const productInformationConfig = {
      ...PRODUCT_INFORMATION,
      label: productInformationLabel,
      description: productInformationInstruction,
      validationMessageKeys: {
        required: productInformationValidationMessage,
      },
      onChange: this.onChangeProductInformation,
    };

    const productQuantityConfig = {
      ...PRODUCT_QUANTITY,
      label: quantityLabel,
      onChange: this.onChangeProductQuantity,
    };

    const cancelButtonConfig = {
      ...CANCEL_BUTTON,
      onClick: onClickCancelHandler,
    };

    const formFieldsConfigs = [
      products.length !== 1 && productTypeConfig,
      hasProductSelectionType && productSelectionTypesConfig,
      productQuantityConfig,
      productInformationConfig,
      cancelButtonConfig,
    ].filter(Boolean);

    const initialValues = {
      productId: fields.id,
      selectionTypeId: fields.selectionTypeId,
      information: fields.information,
      quantity: fields.quantity,
    };

    return (
      <div className={styles.ProductsForm} data-testid="ProductsForm">
        <Messages forTypes={[errorTypes.NO_SELECTED_PRODUCTS]} />
        <Form
          key={hasProductSelectionType}
          onSubmit={this.onSubmitHandler}
          initialValues={initialValues}
          validate={this.validateForm}
          render={({ handleSubmit, form, submitFailed, errors }) => {
            // Store form reference
            this.formRef.current = form;

            // Call onErrorHandler if submit failed with errors
            if (submitFailed && errors && Object.keys(errors).length > 0) {
              this.onErrorHandler();
            }

            return (
              <form onSubmit={handleSubmit} noValidate>
                {formFieldsConfigs.map((fieldConfig) => {
                  // Skip cancel button, render it separately
                  if (fieldConfig.id === CANCEL_BUTTON.id || fieldConfig.onClick) {
                    return null;
                  }

                  const FieldComponent = fieldConfig.component;

                  return (
                    <Field key={fieldConfig.name} name={fieldConfig.name}>
                      {({ input, meta }) => {
                        const showError = meta.touched && meta.error;

                        return (
                          <FieldComponent
                            {...input}
                            id={fieldConfig.id}
                            label={fieldConfig.label}
                            labelText={fieldConfig.labelText}
                            type={fieldConfig.type}
                            options={fieldConfig.options}
                            required={fieldConfig.required}
                            maxLength={fieldConfig.maxLength}
                            description={fieldConfig.description}
                            error={showError ? meta.error : undefined}
                            className={fieldConfig.className}
                            onChange={(e) => {
                              const value = fieldConfig.normalize
                                ? fieldConfig.normalize(e.target.value)
                                : e.target.value;
                              input.onChange(value);
                              if (fieldConfig.onChange) {
                                fieldConfig.onChange(e);
                              }
                            }}
                          />
                        );
                      }}
                    </Field>
                  );
                })}
                <div className={styles.ProductsForm__buttons}>
                  <button type="submit" className="jl-button jl-button--primary">
                    {submitButtonTitle}
                  </button>
                  {cancelButtonConfig.onClick && (
                    <button
                      type="button"
                      onClick={cancelButtonConfig.onClick}
                      className="jl-button jl-button--secondary"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            );
          }}
        />
      </div>
    );
  }
}

export const MemoizedProductsForm = memo(ProductsForm);

ProductsForm.propTypes = {
  products: arrayOf(
    shape({
      id: string,
      name: string,
      price: string,
    }),
  ).isRequired,
  configs: PropTypes.shape({}).isRequired,
  onClickCancelHandler: PropTypes.func,
  clearErrorMessages: PropTypes.func.isRequired,
  editableProduct: shape({}),
  id: string,
  selectionType: shape({}),
  information: string,
  quantity: string,
  onSubmitHandler: PropTypes.func.isRequired,
};

ProductsForm.defaultProps = {
  editableProduct: null,
};
